/**
 * @module components/solid/SolidView
 * @summary The Solid mode surface: a canvas owned by `solidEngine`, with a
 *   feature tree down the left and the selected feature's dimensions down the
 *   right.
 * @description
 *   Follows `Canvas3DView`'s contract exactly — React renders the DOM once and
 *   the engine owns the canvas from then on, so the render loop is never
 *   disturbed by a re-render. Everything reactive is chrome: the tree, the
 *   parameter panel and the read-out, all driven off one `sync` that pulls from
 *   the engine.
 *
 *   Mounted OVER the editor like the 3D view, so the 2D engine keeps its
 *   document, its selection and its autosave while a part is being modelled.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { solidEngine, SNAPS, PLANE_VIEW, type SketchState } from '../../engine3d/solid/SolidEngine';
import {
  KINDS,
  KIND_ORDER,
  PART_MATERIALS,
  sketchParams,
  type Feature,
  type FeatureKind,
  type ParamSpec,
  type Part,
  type Sketch,
} from '../../engine3d/solid/part';
import { TEMPLATES } from '../../engine3d/solid/presets';
import type { MatKey } from '../../engine3d/materials';
import type { ViewPreset } from '../../engine3d/camera';
import '../../styles/view3d.css';
import '../../styles/solid.css';

const PRESETS: { id: ViewPreset; label: string; title: string }[] = [
  { id: 'iso', label: 'Iso', title: 'True isometric — equal lengths measure equal' },
  { id: 'isoSW', label: 'SW', title: 'Three-quarter view from the south-west' },
  { id: 'top', label: 'Plan', title: 'Straight down' },
  { id: 'front', label: 'Front', title: 'Front elevation' },
  { id: 'side', label: 'Side', title: 'Side elevation' },
];

/**
 * The three ways a drawn outline becomes a solid, and the plane each one starts
 * on. Revolve is pinned to the front plane: its horizontal ordinate is a radius
 * about the vertical axis, which only reads as a profile stood upright.
 */
const SKETCH_OPS: { op: Sketch['op']; plane: Sketch['plane']; label: string; icon: string; blurb: string }[] = [
  {
    op: 'extrude',
    plane: 'xz',
    label: 'Extrude',
    icon: '⬍',
    blurb: 'Draw an outline and pull it up into a solid — brackets, plates, odd-shaped hoppers',
  },
  {
    op: 'revolve',
    plane: 'xy',
    label: 'Revolve',
    icon: '⟳',
    blurb: 'Draw half a section and spin it about the vertical axis — anything rolled or turned',
  },
  {
    op: 'sweep',
    plane: 'xz',
    label: 'Sweep',
    icon: '⌇',
    blurb: 'Draw a route and run a round duct along it, with bends where it turns',
  },
];

const PLANES: Sketch['plane'][] = ['xz', 'xy', 'zy'];

/** Millimetres, shown the way a drawing writes them. */
function mm(v: number): string {
  if (Math.abs(v) >= 1000) {
    const m = v / 1000;
    return `${m.toFixed(m >= 10 ? 2 : 3).replace(/\.?0+$/, '')} m`;
  }
  return `${Math.round(v)} mm`;
}

/**
 * One dimension control: a slider for feel, a number box for precision.
 * Enumerated parameters get a dropdown instead — dragging a slider between
 * "flat" and "dished" is not a dimension.
 */
function ParamRow({
  spec,
  value,
  onChange,
}: {
  spec: ParamSpec;
  value: number;
  onChange: (v: number) => void;
}) {
  if (spec.choices) {
    return (
      <label className="sw-row" title={spec.hint}>
        <span className="sw-row-lbl">{spec.label}</span>
        <select
          className="sw-select"
          value={String(value)}
          onChange={(e) => onChange(Number(e.target.value))}
        >
          {spec.choices.map((c) => (
            <option key={c.v} value={c.v}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  const unit = spec.unit === 'mm' ? 'mm' : spec.unit === 'deg' ? '°' : spec.unit === 'x' ? '×' : '';
  return (
    <label className="sw-row" title={spec.hint}>
      <span className="sw-row-lbl">{spec.label}</span>
      <input
        className="sw-slider-in"
        type="range"
        min={spec.min}
        max={spec.max}
        step={spec.step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="sw-num-wrap">
        <input
          className="sw-num"
          type="number"
          min={spec.min}
          max={spec.max}
          step={spec.step}
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) onChange(v);
          }}
        />
        <span className="sw-unit">{unit}</span>
      </span>
    </label>
  );
}

/** X / Y / Z (or rotation) in one line of three boxes. */
function TripleRow({
  label,
  keys,
  feature,
  unit,
  step,
  onChange,
}: {
  label: string;
  keys: ('x' | 'y' | 'z' | 'rx' | 'ry' | 'rz')[];
  feature: Feature;
  unit: string;
  step: number;
  onChange: (k: 'x' | 'y' | 'z' | 'rx' | 'ry' | 'rz', v: number) => void;
}) {
  return (
    <div className="sw-row">
      <span className="sw-row-lbl">{label}</span>
      <div className="sw-triple">
        {keys.map((k) => (
          <span key={k} className="sw-num-wrap">
            <input
              className="sw-num"
              type="number"
              step={step}
              value={feature[k]}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v)) onChange(k, v);
              }}
            />
            <span className="sw-unit">{unit}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function SolidView() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const loadRef = useRef<HTMLInputElement>(null);

  const [part, setPart] = useState<Part>(solidEngine.getPart());
  const [sel, setSel] = useState<Feature | null>(null);
  const [stats, setStats] = useState(solidEngine.getStats());
  const [flags, setFlags] = useState({ ...solidEngine.opts });
  const [proj, setProj] = useState(solidEngine.getProjection());
  const [preset, setPreset] = useState<ViewPreset>('isoSW');
  const [snapMm, setSnapMm] = useState(solidEngine.snapMm);
  const [history, setHistory] = useState({ undo: false, redo: false });
  const [addOpen, setAddOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [hint, setHint] = useState(true);
  const [sketch, setSketch] = useState<SketchState | null>(null);
  const [sketchOpen, setSketchOpen] = useState(false);

  const sync = useCallback(() => {
    setPart({ ...solidEngine.getPart() });
    setSel(solidEngine.getSelected());
    setHistory({ undo: solidEngine.canUndo(), redo: solidEngine.canRedo() });
    setSnapMm(solidEngine.snapMm);
    // Copied out rather than referenced: the engine mutates its sketch in place
    // as points land, and React would not see a mutation of the same object.
    const sk = solidEngine.getSketch();
    setSketch(sk ? { ...sk, pts: sk.pts.slice() } : null);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    solidEngine.mount(canvas, wrap);
    setProj(solidEngine.getProjection());
    setFlags({ ...solidEngine.opts });
    sync();
    // The read-out only has to feel live — polling beats re-rendering React on
    // every one of sixty frames a second.
    const timer = window.setInterval(() => setStats(solidEngine.getStats()), 400);
    const offFrame = solidEngine.onFrame(sync);
    const dismiss = () => setHint(false);
    canvas.addEventListener('pointerdown', dismiss, { once: true });
    return () => {
      window.clearInterval(timer);
      offFrame();
      canvas.removeEventListener('pointerdown', dismiss);
      solidEngine.unmount();
    };
  }, [sync]);

  const applyPreset = (p: ViewPreset) => {
    solidEngine.setPreset(p);
    setPreset(p);
    setProj(solidEngine.getProjection());
  };

  const toggle = (k: 'grid' | 'shadows' | 'labels' | 'isolate') => {
    solidEngine.toggle(k);
    setFlags({ ...solidEngine.opts });
  };

  const selId = sel?.id ?? '';
  const spec = sel ? KINDS[sel.kind] : null;

  return (
    <div id="solidview" ref={wrapRef}>
      <canvas id="solid-canvas" ref={canvasRef} />

      {/* The empty page. A blank grid with no explanation reads as broken, so
          the placeholder says what this mode is and offers the two ways in. It
          is click-through except on its own buttons, so the floor can still be
          orbited underneath. */}
      {!part.features.length && !sketch && (
        <div className="sw-empty">
          <strong>Empty part</strong>
          <p>
            Build equipment from parametric solids — a hopper, a feeder, a chute, a stand — then
            export it as an STL or save it as a part file.
          </p>
          <span className="sw-empty-act">
            <button className="sw-btn" onClick={() => setAddOpen(true)}>
              ✚ Add a feature
            </button>
            <button className="sw-btn" onClick={() => setTplOpen(true)}>
              ▤ Start from a template
            </button>
          </span>
        </div>
      )}

      {/* ── Feature tree ── */}
      <aside className="sw-panel sw-left">
        <header className="sw-panel-hd">
          <input
            className="sw-name"
            value={part.name}
            placeholder="Untitled part"
            title="Part name — used for the exported file"
            onChange={(e) => {
              solidEngine.setPartName(e.target.value);
              setPart({ ...solidEngine.getPart() });
            }}
          />
          <span className="sw-count">{stats.features} features</span>
        </header>

        <div className="sw-tree">
          {!part.features.length && (
            <div className="sw-tree-empty">
              No features yet — start from a template, or add a primitive below.
            </div>
          )}
          {part.features.map((f, i) => (
            <div
              key={f.id}
              className={
                'sw-node' + (f.id === selId ? ' sel' : '') + (f.suppressed ? ' supp' : '')
              }
              onClick={() => solidEngine.select(f.id)}
              onDoubleClick={() => {
                solidEngine.select(f.id);
                solidEngine.fitSelection();
              }}
              title={KINDS[f.kind].blurb}
            >
              <span className="sw-node-icon">{KINDS[f.kind].icon}</span>
              <span className="sw-node-name">{f.name}</span>
              {f.pattern && (
                <span className="sw-badge" title="Patterned">
                  ×{Math.max(1, f.pattern.count) * Math.max(1, f.pattern.count2 ?? 1)}
                </span>
              )}
              <span className="sw-node-tools">
                <button
                  title={f.suppressed ? 'Unsuppress' : 'Suppress — keep the row, skip the solid'}
                  onClick={(e) => {
                    e.stopPropagation();
                    solidEngine.toggleSuppressed(f.id);
                  }}
                >
                  {f.suppressed ? '◌' : '◉'}
                </button>
                <button
                  title="Move up"
                  disabled={i === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    solidEngine.reorder(f.id, -1);
                  }}
                >
                  ▲
                </button>
                <button
                  title="Move down"
                  disabled={i === part.features.length - 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    solidEngine.reorder(f.id, 1);
                  }}
                >
                  ▼
                </button>
                <button
                  title="Delete (Del)"
                  className="sw-del"
                  onClick={(e) => {
                    e.stopPropagation();
                    solidEngine.remove(f.id);
                  }}
                >
                  ✕
                </button>
              </span>
            </div>
          ))}
        </div>

        <footer className="sw-panel-ft">
          <button className="sw-btn wide" onClick={() => setAddOpen((v) => !v)}>
            ✚ Add feature
          </button>
          {addOpen && (
            <div className="sw-grid">
              {KIND_ORDER.map((k) => (
                <button
                  key={k}
                  className="sw-tile"
                  title={KINDS[k].blurb}
                  onClick={() => {
                    solidEngine.addFeature(k as FeatureKind);
                    setAddOpen(false);
                  }}
                >
                  <span className="sw-tile-icon">{KINDS[k].icon}</span>
                  <span>{KINDS[k].label}</span>
                </button>
              ))}
            </div>
          )}
        </footer>
      </aside>

      {/* ── Parameters ── */}
      <aside className="sw-panel sw-right">
        {!sel || !spec ? (
          <div className="sw-nosel">
            <strong>Nothing selected</strong>
            <p>
              Click a feature in the view or in the tree to edit its dimensions. Drag a selected
              feature to move it on the floor; hold <kbd>Shift</kbd> while dragging to raise it.
            </p>
          </div>
        ) : (
          <>
            <header className="sw-panel-hd">
              {/* Keyed on the selection alone, so it re-seeds when a
                  different feature is picked and never mid-word. */}
              <input
                key={selId}
                className="sw-name"
                defaultValue={sel.name}
                title="Feature name"
                onChange={(e) => solidEngine.rename(selId, e.target.value)}
              />
              <span className="sw-count">{spec.label}</span>
            </header>

            <div className="sw-body">
              <div className="sw-sect">Dimensions</div>
              {/* A sketch is dimensioned by its operation: offering a sweep
                  radius on an extrusion is a control that does nothing, which is
                  the worst kind to put in front of an engineer. */}
              {(sel.sketch
                ? spec.params.filter((ps) => sketchParams(sel.sketch!.op).includes(ps.key))
                : spec.params
              ).map((ps) => (
                <ParamRow
                  key={ps.key}
                  spec={ps}
                  value={sel.p[ps.key] ?? 0}
                  onChange={(v) => solidEngine.setParam(selId, ps.key, v)}
                />
              ))}

              {sel.sketch && (
                <>
                  <div className="sw-sect">Profile</div>
                  <div className="sw-row">
                    <span className="sw-row-lbl">Drawn on</span>
                    <span className="sw-readout">
                      {PLANE_VIEW[sel.sketch.plane].label} · {sel.sketch.pts.length} points ·{' '}
                      {sel.sketch.op}
                    </span>
                  </div>
                  <div className="sw-row">
                    <span className="sw-row-lbl" />
                    <div className="sw-inline">
                      <button
                        className="sw-btn"
                        title="Re-open the outline and change it"
                        onClick={() => solidEngine.editSketch(selId)}
                      >
                        ✎ Edit profile
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div className="sw-sect">Placement</div>
              <TripleRow
                label="Position"
                keys={['x', 'y', 'z']}
                feature={sel}
                unit="mm"
                step={snapMm || 10}
                onChange={(k, v) => solidEngine.setPlacement(selId, k, v)}
              />
              <TripleRow
                label="Rotation"
                keys={['rx', 'ry', 'rz']}
                feature={sel}
                unit="°"
                step={15}
                onChange={(k, v) => solidEngine.setPlacement(selId, k, v)}
              />
              <div className="sw-row">
                <span className="sw-row-lbl" />
                <div className="sw-inline">
                  <button
                    className="sw-btn"
                    title="Sit this feature on the floor"
                    onClick={() => solidEngine.dropToFloor(selId)}
                  >
                    ⤓ Drop to floor
                  </button>
                  <button
                    className="sw-btn"
                    title="Centre it on the part axis"
                    onClick={() => solidEngine.centreOnAxis(selId)}
                  >
                    ⌖ Centre
                  </button>
                </div>
              </div>

              <div className="sw-sect">Material</div>
              <div className="sw-row">
                <span className="sw-row-lbl">Finish</span>
                <select
                  className="sw-select wide"
                  value={sel.mat}
                  onChange={(e) => solidEngine.setMaterial(selId, e.target.value as MatKey)}
                >
                  {PART_MATERIALS.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sw-sect">
                Pattern
                <button
                  className="sw-mini"
                  title={sel.pattern ? 'Remove the pattern' : 'Repeat this feature'}
                  onClick={() =>
                    solidEngine.setPattern(
                      selId,
                      sel.pattern ? undefined : { kind: 'linear', count: 2, spacing: 500, axis: 'x' },
                    )
                  }
                >
                  {sel.pattern ? 'Remove' : 'Add'}
                </button>
              </div>
              {sel.pattern && (
                <>
                  <div className="sw-row">
                    <span className="sw-row-lbl">Type</span>
                    <select
                      className="sw-select"
                      value={sel.pattern.kind}
                      onChange={(e) =>
                        solidEngine.updatePattern(selId, {
                          kind: e.target.value as 'linear' | 'circular',
                        })
                      }
                    >
                      <option value="linear">Linear</option>
                      <option value="circular">Circular</option>
                    </select>
                  </div>
                  <ParamRow
                    spec={{ key: 'count', label: 'Count', min: 1, max: 64, step: 1, unit: '' }}
                    value={sel.pattern.count}
                    onChange={(v) => solidEngine.updatePattern(selId, { count: v })}
                  />
                  <ParamRow
                    spec={{
                      key: 'spacing',
                      label: sel.pattern.kind === 'circular' ? 'Radius' : 'Pitch',
                      min: 0,
                      max: 8000,
                      step: 5,
                      unit: 'mm',
                    }}
                    value={sel.pattern.spacing}
                    onChange={(v) => solidEngine.updatePattern(selId, { spacing: v })}
                  />
                  {sel.pattern.kind === 'linear' ? (
                    <>
                      <div className="sw-row">
                        <span className="sw-row-lbl">Along</span>
                        <select
                          className="sw-select"
                          value={sel.pattern.axis}
                          onChange={(e) =>
                            solidEngine.updatePattern(selId, {
                              axis: e.target.value as 'x' | 'y' | 'z',
                            })
                          }
                        >
                          <option value="x">X</option>
                          <option value="y">Y (up)</option>
                          <option value="z">Z</option>
                        </select>
                      </div>
                      <ParamRow
                        spec={{ key: 'count2', label: '2nd count', min: 1, max: 32, step: 1, unit: '' }}
                        value={sel.pattern.count2 ?? 1}
                        onChange={(v) => solidEngine.updatePattern(selId, { count2: v })}
                      />
                      <ParamRow
                        spec={{ key: 'spacing2', label: '2nd pitch', min: 0, max: 8000, step: 5, unit: 'mm' }}
                        value={sel.pattern.spacing2 ?? 0}
                        onChange={(v) => solidEngine.updatePattern(selId, { spacing2: v })}
                      />
                      <div className="sw-row">
                        <span className="sw-row-lbl">2nd along</span>
                        <select
                          className="sw-select"
                          value={sel.pattern.axis2 ?? 'z'}
                          onChange={(e) =>
                            solidEngine.updatePattern(selId, {
                              axis2: e.target.value as 'x' | 'y' | 'z',
                            })
                          }
                        >
                          <option value="x">X</option>
                          <option value="y">Y (up)</option>
                          <option value="z">Z</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <ParamRow
                      spec={{ key: 'sweep', label: 'Sweep', min: 15, max: 360, step: 15, unit: 'deg' }}
                      value={sel.pattern.sweep ?? 360}
                      onChange={(v) => solidEngine.updatePattern(selId, { sweep: v })}
                    />
                  )}
                </>
              )}

              <div className="sw-sect">Feature</div>
              <div className="sw-row">
                <span className="sw-row-lbl" />
                <div className="sw-inline">
                  <button className="sw-btn" title="Duplicate (Ctrl+D)" onClick={() => solidEngine.duplicate(selId)}>
                    ⎘ Duplicate
                  </button>
                  <button className="sw-btn" title="Zoom to this feature (F)" onClick={() => solidEngine.fitSelection()}>
                    ⊙ Zoom to
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </aside>

      {/* ── Top HUD: templates, view, toggles ── */}
      <div className="v3-hud sw-top">
        <div className="v3-group">
          <button
            className={'v3-btn' + (tplOpen ? ' active' : '')}
            title="Start from a ready-made part"
            onClick={() => setTplOpen((v) => !v)}
          >
            ▤ Templates
          </button>
          <span className="v3-sep" />
          <button className="v3-btn" title="Undo (Ctrl+Z)" disabled={!history.undo} onClick={() => solidEngine.undo()}>
            ↩
          </button>
          <button className="v3-btn" title="Redo (Ctrl+Y)" disabled={!history.redo} onClick={() => solidEngine.redo()}>
            ↪
          </button>
          <span className="v3-sep" />
          <button
            className={'v3-btn' + (sketchOpen || sketch ? ' active' : '')}
            title="Draw your own profile, then extrude, revolve or sweep it"
            onClick={() => {
              if (sketch) solidEngine.cancelSketch();
              else setSketchOpen((v) => !v);
            }}
          >
            ✎ Sketch
          </button>
          <span className="v3-sep" />
          <button className="v3-btn" title="Empty the modeller and start a new part" onClick={() => solidEngine.newPart()}>
            ✚ New
          </button>
        </div>

        <div className="v3-group">
          <span className="v3-lbl">View</span>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              className={'v3-btn' + (preset === p.id ? ' active' : '')}
              title={p.title}
              onClick={() => applyPreset(p.id)}
            >
              {p.label}
            </button>
          ))}
          <span className="v3-sep" />
          <button
            className={'v3-btn' + (proj === 'ortho' ? ' active' : '')}
            title="Orthographic — the projection a drawing is dimensioned in"
            onClick={() => {
              const k = proj === 'ortho' ? 'perspective' : 'ortho';
              solidEngine.setProjection(k);
              setProj(k);
            }}
          >
            ⊞ Ortho
          </button>
          <button className="v3-btn" title="Fit the whole part (F)" onClick={() => solidEngine.fitView()}>
            ⊡ Fit
          </button>
        </div>

        <div className="v3-group">
          <button className={'v3-btn' + (flags.grid ? ' active' : '')} title="Floor grid" onClick={() => toggle('grid')}>
            ⠿
          </button>
          <button
            className={'v3-btn' + (flags.shadows ? ' active' : '')}
            title="Soft shadows"
            onClick={() => toggle('shadows')}
          >
            ◐
          </button>
          <button
            className={'v3-btn' + (flags.labels ? ' active' : '')}
            title="Feature names in the view"
            onClick={() => toggle('labels')}
          >
            🏷
          </button>
          <button
            className={'v3-btn' + (flags.isolate ? ' active' : '')}
            title="Ghost everything except the selected feature"
            onClick={() => toggle('isolate')}
          >
            ◍
          </button>
          <span className="v3-sep" />
          <span className="v3-lbl">Snap</span>
          <select
            className="sw-select"
            value={snapMm}
            title="Snap step for dragging and the arrow keys"
            onChange={(e) => {
              solidEngine.snapMm = Number(e.target.value);
              setSnapMm(solidEngine.snapMm);
            }}
          >
            {SNAPS.map((s) => (
              <option key={s} value={s}>
                {s === 0 ? 'Off' : `${s} mm`}
              </option>
            ))}
          </select>
        </div>

        <div className="v3-group">
          <button className="v3-btn" title="Save the editable part as JSON" onClick={() => solidEngine.exportJson()}>
            💾 Part
          </button>
          <button className="v3-btn" title="Open a .part.json" onClick={() => loadRef.current?.click()}>
            📂
          </button>
          <button
            className="v3-btn"
            title="Export a binary STL in millimetres — opens in SolidWorks, Inventor, Fusion, FreeCAD or a slicer"
            onClick={() => solidEngine.exportStl()}
          >
            ⬇ STL
          </button>
          <button className="v3-btn" title="Snapshot the view as a PNG" onClick={() => solidEngine.exportPng()}>
            🖼
          </button>
          <input
            ref={loadRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void solidEngine.importJson(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {/* ── Sketching ── */}
      {sketchOpen && !sketch && (
        <div className="sw-sketchpick">
          <div className="sw-tpl-hd">
            <strong>Draw it yourself</strong>
            <span>Sketch an outline in millimetres, then turn it into a solid.</span>
            <button className="sw-mini" onClick={() => setSketchOpen(false)}>
              ✕ Close
            </button>
          </div>
          <div className="sw-sketchops">
            {SKETCH_OPS.map((o) => (
              <button
                key={o.op}
                className="sw-tpl-card sw-opcard"
                onClick={() => {
                  solidEngine.beginSketch(o.plane, o.op);
                  setSketchOpen(false);
                }}
              >
                <span className="sw-tpl-icon">{o.icon}</span>
                <span className="sw-tpl-name">{o.label}</span>
                <span className="sw-tpl-blurb">{o.blurb}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {sketch && (
        <div className="sw-sketchbar">
          <div className="sw-sketchbar-row">
            <span className="v3-lbl">Sketch</span>
            {SKETCH_OPS.map((o) => (
              <button
                key={o.op}
                className={'v3-btn' + (sketch.op === o.op ? ' active' : '')}
                title={o.blurb}
                onClick={() => solidEngine.setSketchOp(o.op)}
              >
                {o.icon} {o.label}
              </button>
            ))}
            <span className="v3-sep" />
            <span className="v3-lbl">Plane</span>
            {PLANES.map((pl) => (
              <button
                key={pl}
                className={'v3-btn' + (sketch.plane === pl ? ' active' : '')}
                disabled={sketch.op === 'revolve'}
                title={
                  sketch.op === 'revolve'
                    ? 'A revolve is always drawn on the front plane'
                    : `Draw on the ${PLANE_VIEW[pl].label} plane — the solid grows ${PLANE_VIEW[pl].grows}`
                }
                onClick={() => solidEngine.setSketchPlane(pl)}
              >
                {PLANE_VIEW[pl].label}
              </button>
            ))}
            <span className="v3-sep" />
            {/* The bar covers the toolbar while it is up, and snap is the one
                setting that matters mid-outline — so it comes along. */}
            <span className="v3-lbl">Snap</span>
            <select
              className="sw-select"
              value={snapMm}
              title="Points land on this step"
              onChange={(e) => {
                solidEngine.snapMm = Number(e.target.value);
                setSnapMm(solidEngine.snapMm);
              }}
            >
              {SNAPS.map((sv) => (
                <option key={sv} value={sv}>
                  {sv === 0 ? 'Off' : `${sv} mm`}
                </option>
              ))}
            </select>
            <span className="v3-sep" />
            <span className="sw-ptcount">
              <b>{sketch.pts.length}</b> {sketch.pts.length === 1 ? 'point' : 'points'}
            </span>
            <button
              className="v3-btn"
              title="Remove the last point (Backspace)"
              disabled={!sketch.pts.length}
              onClick={() => solidEngine.undoSketchPoint()}
            >
              ⌫ Undo point
            </button>
            <button
              className="v3-btn sw-ok"
              title="Turn the outline into a solid (Enter)"
              disabled={sketch.pts.length < (sketch.op === 'sweep' ? 2 : 3)}
              onClick={() => solidEngine.finishSketch()}
            >
              ✓ {sketch.editing ? 'Update' : 'Make solid'}
            </button>
            <button className="v3-btn sw-no" title="Discard the sketch (Esc)" onClick={() => solidEngine.cancelSketch()}>
              ✕ Cancel
            </button>
          </div>
          <div className="sw-sketchbar-hint">
            Click to place a point.{' '}
            {sketch.op === 'sweep'
              ? 'Press Enter or double-click to finish the route.'
              : 'Close the outline on its first point, or press Enter to finish.'}{' '}
            Right-drag pans, the wheel zooms, and points snap to{' '}
            {snapMm ? `${snapMm} mm` : 'the nearest millimetre'}.
          </div>
        </div>
      )}

      {/* ── Template picker ── */}
      {tplOpen && (
        <div className="sw-tpl">
          <div className="sw-tpl-hd">
            <strong>Templates</strong>
            <span>
              Each one lands as an editable feature stack — replace the part, or add it alongside.
            </span>
            <button className="sw-mini" onClick={() => setTplOpen(false)}>
              ✕ Close
            </button>
          </div>
          <div className="sw-tpl-grid">
            {TEMPLATES.map((t) => (
              <div key={t.id} className="sw-tpl-card">
                <span className="sw-tpl-icon">{t.icon}</span>
                <span className="sw-tpl-name">{t.label}</span>
                <span className="sw-tpl-blurb">{t.blurb}</span>
                <span className="sw-tpl-act">
                  <button
                    className="sw-btn"
                    title="Replace the current part with this one"
                    onClick={() => {
                      solidEngine.loadTemplate(t.id);
                      setTplOpen(false);
                    }}
                  >
                    Open
                  </button>
                  <button
                    className="sw-btn"
                    title="Add it beside what is already modelled"
                    onClick={() => {
                      solidEngine.insertTemplate(t.id);
                      setTplOpen(false);
                    }}
                  >
                    ✚ Add
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Read-out ── */}
      <div className="v3-hud sw-bottom">
        <div className="v3-stats">
          <span>
            Size <b>{mm(stats.size.x)}</b> × <b>{mm(stats.size.z)}</b> × <b>{mm(stats.size.y)}</b> high
          </span>
          <span>
            <b>{stats.features}</b> features
          </span>
          <span>
            <b>{stats.instances}</b> solids
          </span>
          <span>
            <b>{stats.triangles.toLocaleString()}</b> tris
          </span>
          <span>
            <b>{stats.fps}</b> fps
          </span>
        </div>
      </div>

      {hint && !!part.features.length && (
        <div className="sw-hint">
          <b>Drag</b> to orbit · <b>right-drag</b> to pan · <b>wheel</b> to zoom · <b>click</b> a
          feature to select · <b>drag it again</b> to move it, <b>Shift+drag</b> to raise it
        </div>
      )}
    </div>
  );
}
